import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import userService, { UserWithRole, Role, RoleHistory } from '../../../shared/services/userService';
import RoleHistoryModal from './components/modals/RoleHistoryModal';
import Pagination from './components/Pagination';

type UserWithReferents = UserWithRole & {
  referents?: { function: string; referent_type: string }[];
};

/**
 * Page dédiée « Gestion des rôles individuels » :
 * liste de toutes les personnes (nom, date création, rôle actuel),
 * select pour changer le rôle, historique (qui a mis le rôle).
 */
const IndividualRoleManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded = embedded || new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('embed') === '1';

  const [users, setUsers] = useState<UserWithReferents[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRoleId, setFilterRoleId] = useState<number | ''>('');
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [historyUser, setHistoryUser] = useState<UserWithRole | null>(null);
  const [roleHistory, setRoleHistory] = useState<RoleHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const hasAccess = currentUser?.roles_id === 1;

  const loadUsers = useCallback(async () => {
    if (!hasAccess) return;
    try {
      setLoading(true);
      setError(null);
      const res = await userService.getUsers({
        page,
        limit: 50,
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
        ...(filterRoleId !== '' && { role: filterRoleId }),
      });
      if (res.success && res.data) {
        setUsers(res.data as UserWithReferents[]);
        setTotalPages(res.pagination?.totalPages ?? 1);
      } else {
        setError('Erreur lors du chargement des utilisateurs');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, page, searchTerm, filterRoleId]);

  const loadRoles = useCallback(async () => {
    try {
      const res = await userService.getRoles();
      if (res.success && res.data) setRoles(res.data);
    } catch {
      setError('Erreur lors du chargement des rôles');
    }
  }, []);

  useEffect(() => {
    if (!hasAccess) {
      navigate('/panel');
      return;
    }
    loadRoles();
  }, [hasAccess, navigate, loadRoles]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const handleFilterChange = (value: number | '') => {
    setFilterRoleId(value);
    setPage(1);
  };

  const handleRoleChange = async (user: UserWithReferents, newRoleId: number) => {
    if (newRoleId === user.roles_id) return;
    try {
      setUpdatingUserId(user.id);
      setError(null);
      const res = await userService.updateUserRole(user.id, newRoleId);
      if (res.success) {
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, roles_id: newRoleId, role_name: res.data?.role_name ?? u.role_name } : u))
        );
      } else {
        setError(res.error || 'Erreur lors du changement de rôle');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du changement de rôle');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleOpenHistory = async (user: UserWithReferents) => {
    setHistoryLoading(true);
    try {
      const res = await userService.getUserRoleHistory(user.id);
      const list = res.success && res.data ? res.data : [];
      setRoleHistory(list);
      setHistoryUser(user);
    } catch {
      setRoleHistory([]);
      setHistoryUser(user);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (!hasAccess) return null;

  return (
    <div className={!isEmbedded ? 'min-h-screen relative overflow-hidden' : ''}>
      {!isEmbedded && <BackgroundImage />}
      {!isEmbedded && <ParticlesBackground />}
      {!isEmbedded && <Navbar />}
      <div className={!isEmbedded ? 'relative z-10 pt-24 pb-16' : ''}>
        <div className={!isEmbedded ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : ''}>
          {!isEmbedded && (
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-cockpit-text mb-2 text-cockpit-text">
                Gestion des rôles individuels
              </h1>
              <p className="text-cockpit-text-2">
                Liste des personnes sur le jeu : modifiez le rôle et consultez l'historique des changements.
              </p>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-[#6366f1] text-cockpit-text rounded-lg font-semibold hover:bg-[#5457e6] transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour au panel</span>
            </button>
          </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 backdrop-blur-sm overflow-x-auto">
            {/* Barre de recherche et filtre */}
            <div className="p-4 border-b border-[#1a2537] flex flex-wrap items-center gap-4">
              <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px] flex gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par nom d'utilisateur..."
                  className="flex-1 rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
                />
                <button
                  type="submit"
                  className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-medium text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors whitespace-nowrap"
                >
                  Rechercher
                </button>
              </form>
              <div className="flex items-center gap-2">
                <label htmlFor="filter-role" className="text-cockpit-text-2 text-sm font-medium whitespace-nowrap">
                  Rôle :
                </label>
                <select
                  id="filter-role"
                  value={filterRoleId}
                  onChange={(e) => handleFilterChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  className="rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text focus:outline-none focus:border-[#6366f1]/60 min-w-[180px]"
                >
                  <option value="">Tous les rôles</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.role_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-4">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#1a2537]">
                      <th className="pb-3 pr-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom d'utilisateur</th>
                      <th className="pb-3 pr-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Créé le</th>
                      <th className="pb-3 pr-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Rôle actuel</th>
                      <th className="pb-3 pr-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Référent(s)</th>
                      <th className="pb-3 pr-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Changer le rôle</th>
                      <th className="pb-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Historique</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                        <td className="py-3 pr-4 text-cockpit-text font-medium">{user.username}</td>
                        <td className="py-3 pr-4 text-cockpit-text-2 text-sm">
                          {user.created_at
                            ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : '–'}
                        </td>
                        <td className="py-3 pr-4 text-cockpit-text-2 text-sm">{user.role_name ?? '–'}</td>
                        <td className="py-3 pr-4">
                          {user.referents?.length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {user.referents.map((ref) => (
                                <span
                                  key={ref.function}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                                  title={ref.referent_type}
                                >
                                  {ref.function}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-cockpit-muted">–</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          {user.id === currentUser?.id ? (
                            <span className="text-cockpit-muted italic text-sm">Vous (non modifiable)</span>
                          ) : (
                            <>
                              <select
                                value={user.roles_id}
                                onChange={(e) => handleRoleChange(user, parseInt(e.target.value, 10))}
                                disabled={updatingUserId === user.id}
                                className="rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text focus:outline-none focus:border-[#6366f1]/60 disabled:opacity-40 min-w-[160px]"
                              >
                                {roles
                                  .filter(
                                    (r) =>
                                      r.id > (currentUser?.roles_id ?? 99) || r.id === user.roles_id
                                  )
                                  .map((r) => (
                                    <option key={r.id} value={r.id}>
                                      {r.role_name}
                                    </option>
                                  ))}
                              </select>
                              {updatingUserId === user.id && (
                                <span className="ml-2 text-cockpit-muted text-sm">...</span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-3">
                          <button
                            type="button"
                            onClick={() => handleOpenHistory(user)}
                            className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1.5 text-sm font-medium text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
                          >
                            Historique
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {historyUser && (
        <RoleHistoryModal
          user={historyUser}
          history={historyLoading ? [] : roleHistory}
          onClose={() => {
            setHistoryUser(null);
            setRoleHistory([]);
          }}
        />
      )}
    </div>
  );
};

export default IndividualRoleManagement;
